import { z } from 'zod'

/**
 * SPEC.md §4.5 — FiveM player identifiers.
 *
 * Values are stored and transmitted BARE: `{ license: 'abc…' }`, never
 * `'license:abc…'`. Only `formatIdentifier()` adds the prefix, and only for
 * display or for the `type:value` form used by `players.primary_identifier`.
 */

export const IDENTIFIER_TYPES = ['license', 'license2', 'steam', 'discord', 'fivem', 'ip'] as const
export type IdentifierType = (typeof IDENTIFIER_TYPES)[number]
export const IdentifierTypeSchema = z.enum(IDENTIFIER_TYPES)

/**
 * Identifiers that survive a reinstall and are therefore the only ones matched
 * across servers by the global ban network (SPEC.md §10.6). `ip` and hardware
 * tokens stay local to the issuing server.
 */
export const HARD_IDENTIFIER_TYPES = ['license', 'license2', 'steam', 'discord', 'fivem'] as const
export type HardIdentifierType = (typeof HARD_IDENTIFIER_TYPES)[number]
export const HardIdentifierTypeSchema = z.enum(HARD_IDENTIFIER_TYPES)

export function isHardIdentifier(type: string): type is HardIdentifierType {
  return (HARD_IDENTIFIER_TYPES as readonly string[]).includes(type)
}

/** Priority order when picking `players.primary_identifier`. */
export const PRIMARY_IDENTIFIER_ORDER = ['license', 'license2', 'fivem', 'steam', 'discord'] as const

const idValue = z.string().min(1).max(128)

export const IdentifiersSchema = z
  .object({
    license: idValue,
    license2: idValue,
    steam: idValue,
    discord: idValue,
    fivem: idValue,
    ip: idValue,
  })
  .partial()
export type Identifiers = z.infer<typeof IdentifiersSchema>

/** Hardware tokens from `GetPlayerTokens`. Opaque, capped so a client cannot flood. */
export const TokensSchema = z.array(z.string().min(1).max(128)).max(16)
export type Tokens = z.infer<typeof TokensSchema>

/**
 * Parses raw `GetPlayerIdentifiers` output into the bare-value object.
 *
 * `xbl:` and `live:` are ignored (removed by Cfx on 27 April 2026), as is any
 * unknown type. Splitting on the FIRST colon matters: `license2:` must not be
 * mistaken for `license:`.
 */
export function parseIdentifiers(raw: readonly string[] | null | undefined): Identifiers {
  const out: Record<string, string> = {}
  if (!raw) return out
  for (const entry of raw) {
    if (typeof entry !== 'string') continue
    const sep = entry.indexOf(':')
    if (sep <= 0) continue
    const type = entry.slice(0, sep).toLowerCase()
    const value = entry.slice(sep + 1).trim()
    if (!value || value.length > 128) continue
    if (!(IDENTIFIER_TYPES as readonly string[]).includes(type)) continue
    // First occurrence wins; FiveM never sends duplicates but a hostile client might.
    if (out[type] === undefined) out[type] = value
  }
  return out as Identifiers
}

export function formatIdentifier(type: IdentifierType, value: string): string {
  return `${type}:${value}`
}

/**
 * The stable per-server key for a player, as `type:value`. Returns null when the
 * connect payload carried no hard identifier at all, which the connect check
 * rejects with `connect_missing_identifier`.
 */
export function primaryIdentifier(ids: Identifiers): string | null {
  for (const type of PRIMARY_IDENTIFIER_ORDER) {
    const value = ids[type]
    if (value) return formatIdentifier(type, value)
  }
  return null
}

/** Flattens to `{ type, value }` pairs for the `player_identifiers` / `ban_identifiers` tables. */
export function identifierPairs(ids: Identifiers): Array<{ type: IdentifierType; value: string; isHard: boolean }> {
  const pairs: Array<{ type: IdentifierType; value: string; isHard: boolean }> = []
  for (const type of IDENTIFIER_TYPES) {
    const value = ids[type]
    if (value) pairs.push({ type, value, isHard: isHardIdentifier(type) })
  }
  return pairs
}

/** True when the two identity sets share at least one hard identifier. */
export function sharesHardIdentifier(a: Identifiers, b: Identifiers): boolean {
  return HARD_IDENTIFIER_TYPES.some((type) => Boolean(a[type]) && a[type] === b[type])
}

/**
 * Masks an identifier for the global ban list, which is visible to every tenant
 * (SPEC.md §10.6): first six characters, then an ellipsis.
 */
export function maskIdentifier(value: string): string {
  if (value.length <= 6) return `${value.slice(0, 2)}…`
  return `${value.slice(0, 6)}…`
}

/** Parses a single free-text identifier a staff member pasted into the ban dialog. */
export function parseIdentifierInput(input: string): { type: IdentifierType; value: string } | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  const sep = trimmed.indexOf(':')
  if (sep > 0) {
    const type = trimmed.slice(0, sep).toLowerCase()
    const value = trimmed.slice(sep + 1).trim()
    if ((IDENTIFIER_TYPES as readonly string[]).includes(type) && value) {
      return { type: type as IdentifierType, value }
    }
    return null
  }
  // Bare values: infer the type from shape where it is unambiguous.
  if (/^\d{17,20}$/.test(trimmed)) return { type: 'discord', value: trimmed }
  if (/^[0-9a-f]{40}$/i.test(trimmed)) return { type: 'license', value: trimmed.toLowerCase() }
  if (/^110000[0-9a-f]{9}$/i.test(trimmed)) return { type: 'steam', value: trimmed.toLowerCase() }
  return null
}
